import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { GisavieditorComponent } from 'src/app/gisavieditor/gisavieditor.component';
import { LrsDashboardPanelComponent } from 'src/app/components/lrs/lrs-dashboard-panel/lrs-dashboard-panel.component';
import { GisFocusService } from 'src/app/shared/services/gis-focus.service';
import { GisBoqWorkbenchService } from 'src/app/shared/services/gis-boq-workbench.service';
import { GisBoqExplorerComponent } from 'src/app/components/geospatial/gis-boq-workbench/gis-boq-explorer.component';
import { GisBoqParamsComponent } from 'src/app/components/geospatial/gis-boq-workbench/gis-boq-params.component';
import { GisBoqTableComponent } from 'src/app/components/geospatial/gis-boq-workbench/gis-boq-table.component';

@Component({
  selector: 'app-geospatial-shell',
  standalone: true,
  imports: [
    CommonModule,
    GisavieditorComponent,
    LrsDashboardPanelComponent,
    GisBoqExplorerComponent,
    GisBoqParamsComponent,
    GisBoqTableComponent,
  ],
  templateUrl: './geospatial-shell.component.html',
  styleUrls: ['./geospatial-shell.component.css'],
})
export class GeospatialShellComponent implements OnInit, OnDestroy {
  projectId = '';
  projectName = '';
  isGisFocusMode = false;
  lrsDashExpanded = false;
  showLrsPanel = true;
  boqWorkbench = false;
  explorerCollapsed = false;
  paramsCollapsed = false;
  tableExpanded = true;
  @ViewChild(GisavieditorComponent) gisEditor?: GisavieditorComponent;
  private routerSub?: Subscription;
  private focusSub?: Subscription;
  private readonly storageKeys = {
    explorer: 'geo-boq-explorer-collapsed',
    params: 'geo-boq-params-collapsed',
    table: 'geo-boq-table-expanded',
    lrs: 'geo-gis-lrs-expanded',
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly gisFocus: GisFocusService,
    readonly boq: GisBoqWorkbenchService
  ) {}

  ngOnInit(): void {
    this.restoreBoqLayout();
    this.restoreLrsLayout();
    this.syncFromRoute();
    this.routerSub = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(() => this.syncFromRoute());
    this.focusSub = this.gisFocus.isFocusMode$.subscribe((on) => {
      this.isGisFocusMode = on;
    });
  }

  toggleExplorer(): void {
    this.explorerCollapsed = !this.explorerCollapsed;
    this.afterBoqLayoutChange();
  }

  toggleParams(): void {
    this.paramsCollapsed = !this.paramsCollapsed;
    this.afterBoqLayoutChange();
  }

  onTableExpanded(expanded: boolean): void {
    this.tableExpanded = expanded;
    this.afterBoqLayoutChange();
  }

  private afterBoqLayoutChange(): void {
    this.persistBoqLayout();
    this.gisEditor?.refreshMapSize();
  }

  private restoreBoqLayout(): void {
    try {
      this.explorerCollapsed = sessionStorage.getItem(this.storageKeys.explorer) === '1';
      this.paramsCollapsed = sessionStorage.getItem(this.storageKeys.params) === '1';
      this.tableExpanded = sessionStorage.getItem(this.storageKeys.table) !== '0';
      if (
        typeof window !== 'undefined' &&
        window.matchMedia('(max-width: 1100px)').matches &&
        sessionStorage.getItem(this.storageKeys.explorer) == null
      ) {
        this.explorerCollapsed = true;
      }
    } catch {
      /* private mode */
    }
  }

  private restoreLrsLayout(): void {
    try {
      this.lrsDashExpanded = sessionStorage.getItem(this.storageKeys.lrs) === '1';
    } catch {
      this.lrsDashExpanded = false;
    }
  }

  private persistLrsLayout(): void {
    try {
      sessionStorage.setItem(this.storageKeys.lrs, this.lrsDashExpanded ? '1' : '0');
    } catch {
      /* private mode */
    }
  }

  private persistBoqLayout(): void {
    try {
      sessionStorage.setItem(this.storageKeys.explorer, this.explorerCollapsed ? '1' : '0');
      sessionStorage.setItem(this.storageKeys.params, this.paramsCollapsed ? '1' : '0');
      sessionStorage.setItem(this.storageKeys.table, this.tableExpanded ? '1' : '0');
    } catch {
      /* private mode */
    }
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.focusSub?.unsubscribe();
    this.boq.stop();
  }

  enterGisFocusMode(): void {
    this.gisFocus.enter();
  }

  exitGisFocusMode(): void {
    this.gisFocus.exit();
  }

  onLrsDashExpandedChange(expanded: boolean): void {
    this.lrsDashExpanded = expanded;
    this.persistLrsLayout();
    this.gisEditor?.refreshMapSize();
  }

  private syncFromRoute(): void {
    const qp = this.route.snapshot.queryParamMap;
    const routeId = this.route.snapshot.paramMap.get('id');
    this.projectId = qp.get('project_id') || qp.get('projectId') || routeId || '';
    this.projectName = qp.get('project_name') || qp.get('projectName') || '';

    const view = (qp.get('view') || '').toLowerCase();
    const demo = (qp.get('demo') || '').toLowerCase();
    const tab = (qp.get('tab') || '').toLowerCase();

    if (view === 'boq') {
      this.boqWorkbench = true;
      this.showLrsPanel = false;
      this.lrsDashExpanded = false;
      this.boq.start(this.projectId);
      return;
    }

    this.boqWorkbench = false;
    this.boq.stop();

    if (demo === 'lrs' || view === 'lrs') {
      this.showLrsPanel = true;
      this.lrsDashExpanded = true;
      return;
    }

    if (view === 'gis') {
      this.showLrsPanel = false;
      this.lrsDashExpanded = false;
      return;
    }

    this.showLrsPanel = true;
    if (tab === 'lrs') {
      this.lrsDashExpanded = true;
    }
  }

  /** Seeds a demo road network + NH-DEMO-48 and walks Map → Road → LRS gates. */
  launchLrsDemo(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { demo: 'lrs', tab: 'lrs', demo_run: String(Date.now()) },
      queryParamsHandling: 'merge',
    });
  }
}
